/** Shared utilities for the Segment Statistics dashboard tabs. */

export const SEGMENT_COLORS = [
  "#E4002B", "#001A72", "#25282A", "#00B8DD", "#00A651",
  "#FFB114", "#8246AF", "#FF9BB1", "#0078D0", "#E8E3DC",
  "#4DD4EC", "#40C878", "#A878CC", "#FFCB5C", "#FF4D6A",
];

export function segColor(index: number): string {
  return SEGMENT_COLORS[index % SEGMENT_COLORS.length]!;
}

export function safeNum(val: unknown): number {
  const n = Number(val);
  return isFinite(n) ? n : 0;
}

export function formatNumber(n: number): string {
  if (!isFinite(n)) return "0";
  return Math.round(n).toLocaleString();
}

export function fmt1(n: number): string {
  if (!isFinite(n)) return "0.0";
  return n.toFixed(1);
}

export function fmtPct(n: number): string {
  return n.toFixed(2) + "%";
}

// ── Access-level filtering ───────────────────────────────

export type AccessFilter = {
  all: boolean;
  public: boolean;
  semi: boolean;
  priv: boolean;
};

export const ALL_ACCESS: AccessFilter = { all: true, public: true, semi: true, priv: true };

export function canonicalAccess(raw: string): string {
  const s = (raw || "").trim().toLowerCase();
  if (s === "public") return "Public";
  if (s === "semi-public" || s === "semipublic" || s === "semi public") return "Semi-Public";
  if (s === "private") return "Private";
  return raw || "Unknown";
}

export function accessAllowed(accessLevel: string, filter: AccessFilter): boolean {
  if (filter.all) return true;
  const c = canonicalAccess(accessLevel);
  if (c === "Public" && filter.public) return true;
  if (c === "Semi-Public" && filter.semi) return true;
  if (c === "Private" && filter.priv) return true;
  // Unknown or unrecognized access levels pass if any filter is active
  if (c !== "Public" && c !== "Semi-Public" && c !== "Private") return true;
  return false;
}

export function toggleAccessFilter(
  prev: AccessFilter,
  kind: "all" | "public" | "semi" | "priv",
): AccessFilter {
  const next = { ...prev };
  if (kind === "all") {
    const val = !prev.all;
    return { all: val, public: val, semi: val, priv: val };
  }
  next[kind] = !next[kind];
  next.all = next.public && next.semi && next.priv;
  return next;
}

// ── Row field access helper ──────────────────────────────

export type Row = Record<string, unknown>;

/** Try to get a field value from a row, checking multiple possible column names. */
export function getVal(row: Row, columns: string[], ...names: string[]): unknown {
  for (const name of names) {
    if (row[name] !== undefined) return row[name];
    const idx = columns.findIndex(c => c.toLowerCase() === name.toLowerCase());
    if (idx >= 0) {
      const v = row[`data${idx}`];
      if (v !== undefined) return v;
    }
  }
  return undefined;
}

export function getNum(row: Row, columns: string[], ...names: string[]): number {
  return safeNum(getVal(row, columns, ...names));
}

export function getStr(row: Row, columns: string[], ...names: string[]): string {
  const v = getVal(row, columns, ...names);
  return v == null ? "" : String(v);
}

// ── Access-level presence detection ──────────────────────

export function detectAccessPresence(
  rows: Row[],
  columns: string[],
): { public: boolean; semi: boolean; priv: boolean } {
  let pub = false, semi = false, priv = false;
  for (const r of rows) {
    const acc = canonicalAccess(getStr(r, columns, "AccessLevelName", "AccessLevel", "Access Level"));
    if (acc === "Public") pub = true;
    else if (acc === "Semi-Public") semi = true;
    else if (acc === "Private") priv = true;
    if (pub && semi && priv) break;
  }
  return { public: pub, semi: semi, priv: priv };
}

// ── Data compaction ──────────────────────────────────────

export interface CompactItem {
  segment: string;
  value: number;
  extra: number;
  color: string;
  isOther?: boolean;
  otherNames?: string[];
}

/** Fold items below threshold into an "Other" group. */
export function compactSmall<T extends { segment: string }>(
  items: T[],
  getValue: (item: T) => number,
  threshold: number,
  maxKeep = 10,
): CompactItem[] {
  const total = items.reduce((s, it) => s + Math.abs(getValue(it)), 0);
  if (total === 0) return [];

  const sorted = [...items].sort((a, b) => getValue(b) - getValue(a));
  const kept: CompactItem[] = [];
  let otherValue = 0;
  let otherExtra = 0;
  const otherNames: string[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const item = sorted[i]!;
    const val = getValue(item);
    const pct = total > 0 ? (val / total) * 100 : 0;
    if (i < maxKeep && pct >= threshold) {
      kept.push({ segment: item.segment, value: val, extra: 0, color: segColor(i) });
    } else {
      otherValue += val;
      otherNames.push(item.segment);
    }
  }

  if (otherNames.length > 0) {
    kept.push({
      segment: "Other",
      value: otherValue,
      extra: otherExtra,
      color: "#c7c7c7",
      isOther: true,
      otherNames,
    });
  } else {
    // Re-assign colors in case some were skipped
    kept.forEach((k, i) => { k.color = segColor(i); });
  }

  return kept;
}
